import {
  pharmaService,
  type PharmaCompanyCreateRequest,
  type PharmaCompanyInfoResponse,
  type PharmaCompanySearchRequest,
  type PharmaCompanyUpdateRequest,
} from '@/entities/pharma/api/pharma.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const pharmaKeys = {
  all: ['pharma-companies'] as const,
  lists: () => [...pharmaKeys.all, 'list'] as const,
  list: (search: PharmaCompanySearchRequest, pageable: Pageable) =>
    [...pharmaKeys.lists(), search, pageable] as const,
  details: () => [...pharmaKeys.all, 'detail'] as const,
  detail: (pharmaCompanyId: number) =>
    [...pharmaKeys.details(), pharmaCompanyId] as const,
};

export interface PharmaListParams {
  search: PharmaCompanySearchRequest;
  pageable: Pageable;
}

/** 제약사 목록 — 레거시 `pharmaceuticalCompanyInfo.js` */
export const usePharmaCompanyList = ({ search, pageable }: PharmaListParams) =>
  usePaginatedQuery<PharmaCompanySearchRequest, PharmaCompanyInfoResponse>({
    queryKeyPrefix: pharmaKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      pharmaService.getPharmaCompanies(s, p),
  });

/**
 * 제약사 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportPharmaCompanies = () =>
  useMutation({
    mutationFn: (search: PharmaCompanySearchRequest) =>
      pharmaService.exportPharmaCompanies(search),
  });

/** 제약사 상세 — 레거시 `pharmaceuticalCompanyDetails.js` */
export const usePharmaCompanyDetail = (pharmaCompanyId: number | null) =>
  useQuery({
    queryKey:
      pharmaCompanyId != null ? pharmaKeys.detail(pharmaCompanyId) : [],
    queryFn: async () => {
      if (pharmaCompanyId == null) throw new Error('pharmaCompanyId is required');
      return pharmaService.getPharmaCompanyDetail(pharmaCompanyId);
    },
    enabled: pharmaCompanyId != null,
  });

export const useCreatePharmaCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PharmaCompanyCreateRequest) =>
      pharmaService.createPharmaCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pharmaKeys.lists() });
    },
  });
};

export const useUpdatePharmaCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pharmaCompanyId,
      data,
    }: {
      pharmaCompanyId: number;
      data: PharmaCompanyUpdateRequest;
    }) => pharmaService.updatePharmaCompany(pharmaCompanyId, data),
    onSuccess: (_data, { pharmaCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: pharmaKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: pharmaKeys.detail(pharmaCompanyId),
      });
    },
  });
};
